import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import Mascotte from '../Brand/Mascotte';

/* ---------- petits composants ---------- */

const CheckIcon = ({ color = '#F2884B' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
);

/* L'oiseau Phénix — le symbole de l'action (cf. kit mascotte) */
const PhoenixBird = ({ className }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16 28c-1.5-2-3-5-3-8 0-4 2-7 5-9l-1-3c-4 1-7 5-7 10 0 4 2.5 7.5 6 10z" fill="currentColor" opacity="0.9"/>
    <path d="M8 12c2-3 5-5 9-5l-1-3C11 5 7 8 5 13l3-1z" fill="currentColor" opacity="0.7"/>
    <path d="M24 12c-2-3-4-5-7-5l1-3c4 1 7 4 9 9l-3-1z" fill="currentColor" opacity="0.7"/>
    <circle cx="16" cy="6" r="3" fill="currentColor"/>
    <path d="M14 4l-2-3M16 3l0-3M18 4l2-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M13 24c-1 2-2 4-1 6M16 25c0 2 0 5 1 7M19 24c1 2 2 4 1 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

/* Mini-mascotte bébé-oiseau réutilisable dans les scènes */
const MiniMascotte = ({ cx, cy, scale = 1 }) => (
  <g transform={`translate(${cx}, ${cy}) scale(${scale})`}>
    {/* Ombre */}
    <ellipse cx="0" cy="48" rx="18" ry="4" fill="#C9A98E" opacity=".22"/>
    {/* Pieds */}
    <ellipse cx="-8" cy="44" rx="6" ry="4" fill="#E06F37"/>
    <ellipse cx="8" cy="44" rx="6" ry="4" fill="#E06F37"/>
    {/* Crête phénix (3 plumes) */}
    <path d="M0-38 C-3-28-2-22 0-22 C2-22 3-28 0-38Z" fill="#F6D689"/>
    <path d="M-6-34 C-9-27-7-22-4-22 C-3-23-4-28-6-34Z" fill="#EA7637"/>
    <path d="M6-34 C9-27 7-22 4-22 C3-23 4-28 6-34Z" fill="#EA7637"/>
    {/* Corps */}
    <ellipse cx="0" cy="8" rx="22" ry="30" fill="url(#mascotGrad)"/>
    {/* Oreilles */}
    <path d="M-18-8c-5-7-4-14 1-15 5-1 8 5 7 12Z" fill="#EA7637"/>
    <path d="M18-8c5-7 4-14-1-15-5-1-8 5-7 12Z" fill="#EA7637"/>
    {/* Joues */}
    <circle cx="-12" cy="6" r="5" fill="#FBB089" opacity=".65"/>
    <circle cx="12" cy="6" r="5" fill="#FBB089" opacity=".65"/>
    {/* Yeux */}
    <circle cx="-7" cy="-2" r="4.5" fill="#2E2A5E"/>
    <circle cx="7" cy="-2" r="4.5" fill="#2E2A5E"/>
    <circle cx="-5.5" cy="-3.5" r="1.5" fill="#fff"/>
    <circle cx="8.5" cy="-3.5" r="1.5" fill="#fff"/>
    {/* Bouche */}
    <path d="M-5 8 Q0 14 5 8" stroke="#2E2A5E" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    {/* Bras */}
    <path d="M-16 10 q-8 2-10 10" stroke="#EA7637" strokeWidth="5" fill="none" strokeLinecap="round"/>
    <path d="M16 10 q8 2 10 10" stroke="#EA7637" strokeWidth="5" fill="none" strokeLinecap="round"/>
  </g>
);

/* Scène-souvenir illustrée avec la mascotte Titaton */
const MemoryScene = ({ variant = 'park' }) => (
  variant === 'park' ? (
    <svg className="l-scene" viewBox="0 0 300 225" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="mascotGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F89A5E"/>
          <stop offset="1" stopColor="#EA7637"/>
        </linearGradient>
      </defs>
      {/* Ciel */}
      <rect width="300" height="225" fill="#FBE3A0"/>
      {/* Herbe */}
      <rect y="150" width="300" height="75" fill="#B9E0C4"/>
      {/* Soleil */}
      <circle cx="245" cy="48" r="26" fill="#F6D689"/>
      <g stroke="#F2AB63" strokeWidth="3" strokeLinecap="round">
        <line x1="245" y1="8" x2="245" y2="18"/><line x1="245" y1="78" x2="245" y2="88"/>
        <line x1="205" y1="48" x2="215" y2="48"/><line x1="275" y1="48" x2="285" y2="48"/>
      </g>
      {/* Nuage */}
      <ellipse cx="80" cy="42" rx="28" ry="12" fill="#fff" opacity=".6"/>
      <ellipse cx="96" cy="38" rx="18" ry="10" fill="#fff" opacity=".5"/>
      {/* Fleurs */}
      <circle cx="60" cy="158" r="6" fill="#EA5A6B" opacity=".7"/>
      <circle cx="60" cy="158" r="2.5" fill="#F6D689"/>
      <line x1="60" y1="164" x2="60" y2="178" stroke="#5A9E6F" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="240" cy="162" r="5" fill="#3D7BC7" opacity=".6"/>
      <circle cx="240" cy="162" r="2" fill="#F6D689"/>
      <line x1="240" y1="167" x2="240" y2="178" stroke="#5A9E6F" strokeWidth="2" strokeLinecap="round"/>
      {/* Ballon */}
      <ellipse cx="106" cy="68" rx="14" ry="17" fill="#EA5A6B"/>
      <line x1="106" y1="85" x2="112" y2="118" stroke="#302B3B" strokeWidth="1.2"/>
      {/* Mascotte au centre */}
      <MiniMascotte cx={150} cy={145} scale={1.1}/>
      {/* Papillon */}
      <g transform="translate(200,90)">
        <ellipse cx="-5" cy="-2" rx="5" ry="3.5" fill="#E6B24A" opacity=".7"/>
        <ellipse cx="5" cy="-2" rx="5" ry="3.5" fill="#E6B24A" opacity=".7"/>
        <ellipse cx="0" cy="0" rx="1.5" ry="3" fill="#302B3B"/>
      </g>
    </svg>
  ) : (
    <svg className="l-scene" viewBox="0 0 300 225" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="mascotGrad2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F89A5E"/>
          <stop offset="1" stopColor="#EA7637"/>
        </linearGradient>
      </defs>
      {/* Fond intérieur */}
      <rect width="300" height="225" fill="#E1ECF9"/>
      <rect y="160" width="300" height="65" fill="#F0E2D5"/>
      {/* Guirlande */}
      <path d="M30 30 Q75 50 150 28 Q225 6 270 30" stroke="#EA5A6B" strokeWidth="2" fill="none"/>
      <circle cx="60" cy="38" r="4" fill="#3D7BC7"/><circle cx="110" cy="28" r="4" fill="#E6B24A"/>
      <circle cx="150" cy="28" r="4" fill="#EA5A6B"/><circle cx="190" cy="28" r="4" fill="#3D7BC7"/>
      <circle cx="240" cy="34" r="4" fill="#E6B24A"/>
      {/* Gateau */}
      <rect x="115" y="118" width="70" height="44" rx="8" fill="#fff" stroke="#E0C9A8" strokeWidth="2"/>
      <rect x="115" y="118" width="70" height="14" rx="8" fill="#FBE3A0"/>
      <rect x="120" y="140" width="60" height="4" rx="2" fill="#EA5A6B" opacity=".4"/>
      {/* Bougies */}
      <rect x="132" y="98" width="5" height="22" rx="2" fill="#F2884B"/>
      <rect x="148" y="92" width="5" height="28" rx="2" fill="#3D7BC7"/>
      <rect x="164" y="98" width="5" height="22" rx="2" fill="#E6B24A"/>
      {/* Flammes */}
      <ellipse cx="134.5" cy="94" rx="3" ry="5" fill="#FFD54A"/>
      <ellipse cx="150.5" cy="88" rx="3" ry="5" fill="#FFD54A"/>
      <ellipse cx="166.5" cy="94" rx="3" ry="5" fill="#FFD54A"/>
      {/* Mascotte à gauche du gateau */}
      <g transform="translate(0,0)">
        <g>
          {/* Version inline car le gradient id est différent */}
          <g transform="translate(68, 152) scale(0.85)">
            <ellipse cx="0" cy="48" rx="18" ry="4" fill="#C9A98E" opacity=".22"/>
            <ellipse cx="-8" cy="44" rx="6" ry="4" fill="#E06F37"/>
            <ellipse cx="8" cy="44" rx="6" ry="4" fill="#E06F37"/>
            <path d="M0-38 C-3-28-2-22 0-22 C2-22 3-28 0-38Z" fill="#F6D689"/>
            <path d="M-6-34 C-9-27-7-22-4-22 C-3-23-4-28-6-34Z" fill="#EA7637"/>
            <path d="M6-34 C9-27 7-22 4-22 C3-23 4-28 6-34Z" fill="#EA7637"/>
            <ellipse cx="0" cy="8" rx="22" ry="30" fill="url(#mascotGrad2)"/>
            <path d="M-18-8c-5-7-4-14 1-15 5-1 8 5 7 12Z" fill="#EA7637"/>
            <path d="M18-8c5-7 4-14-1-15-5-1-8 5-7 12Z" fill="#EA7637"/>
            <circle cx="-12" cy="6" r="5" fill="#FBB089" opacity=".65"/>
            <circle cx="12" cy="6" r="5" fill="#FBB089" opacity=".65"/>
            <circle cx="-7" cy="-2" r="4.5" fill="#2E2A5E"/>
            <circle cx="7" cy="-2" r="4.5" fill="#2E2A5E"/>
            <circle cx="-5.5" cy="-3.5" r="1.5" fill="#fff"/>
            <circle cx="8.5" cy="-3.5" r="1.5" fill="#fff"/>
            <path d="M-5 8 Q0 14 5 8" stroke="#2E2A5E" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <path d="M-16 10 q-8 2-10 10" stroke="#EA7637" strokeWidth="5" fill="none" strokeLinecap="round"/>
            <path d="M16 10 q8 2 10 10" stroke="#EA7637" strokeWidth="5" fill="none" strokeLinecap="round"/>
          </g>
        </g>
      </g>
      {/* Etoiles festives */}
      <path d="M230 80 l1.2 2.6 2.8.4-2 2 .5 2.8-2.5-1.4-2.5 1.4.5-2.8-2-2 2.8-.4Z" fill="#E6B24A" opacity=".6"/>
      <circle cx="250" cy="110" r="2.5" fill="#EA5A6B" opacity=".5"/>
      <circle cx="42" cy="70" r="2" fill="#3D7BC7" opacity=".5"/>
    </svg>
  )
);

const MoonIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);

/* ---------- page ---------- */

const LandingPage = () => {
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        navRef.current.classList.toggle('l-scrolled', window.scrollY > 40);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const els = document.querySelectorAll('.l-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('l-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="landing">
      {/* NAV */}
      <nav className="l-nav" ref={navRef}>
        <div className="l-nav-inner">
          <Link to="/" className="l-brand"><span className="l-brand-dot"></span>Tita<PhoenixBird className="l-brand-phoenix" />on</Link>
          <div className="l-nav-links">
            <Link to="/login" className="l-nav-login">Se connecter</Link>
            <Link to="/register" className="l-btn l-btn-primary">Commencer</Link>
          </div>
        </div>
      </nav>

      {/* ============ ACTE 1 — HERO : LA THÈSE + LA DÉMO VIVANTE ============ */}
      <section className="l-hero">
        <div className="l-container l-hero-grid">
          <div className="l-hero-text">
            <h1 className="l-reveal">
              Titaton ne stocke pas vos souvenirs.<br/>
              <span className="l-accent">Il joue avec le temps.</span>
            </h1>
            <p className="l-hero-subtitle l-reveal l-d1">
              Ici, les photos vieillissent comme de vraies photos — puis reviennent à la vie.
              Et ce qui compte vraiment part en voyage vers le futur de votre enfant.
            </p>
            <div className="l-hero-ctas l-reveal l-d2">
              <Link to="/register" className="l-btn l-btn-primary">Commencer gratuitement</Link>
              <a href="#phenix" className="l-hero-link">
                Regardez-le revivre
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>
              </a>
            </div>
          </div>

          {/* La démo vivante : un souvenir s'endort, le Phénix passe, la couleur refleurit */}
          <div className="l-hero-visual l-reveal l-d2">
            <div className="l-demo-card" aria-label="Démonstration : un souvenir en noir et blanc redevient coloré au passage du Phénix">
              <div className="l-demo-scene-wrap"><MemoryScene variant="park" /></div>
              <div className="l-demo-badge l-badge-sleep"><MoonIcon /> Souvenir endormi</div>
              <div className="l-demo-badge l-badge-awake">&#10038; ravivé</div>
              <div className="l-demo-phenix">
                <PhoenixBird className="l-demo-bird" />
                <span className="l-ember l-e1"></span>
                <span className="l-ember l-e2"></span>
                <span className="l-ember l-e3"></span>
              </div>
            </div>
            <div className="l-hero-mascot"><Mascotte variant="chargement" size={96} /></div>
          </div>
        </div>
      </section>

      {/* ============ L'AXE DU TEMPS — deux directions ============ */}
      <section className="l-axis">
        <div className="l-container l-axis-grid">
          <a href="#phenix" className="l-axis-card l-reveal">
            <span className="l-axis-arrow">&larr;</span>
            <div>
              <h3>Le souvenir qui revient vers vous</h3>
              <p>Le Phénix ravive vos photos quand vous ne vous y attendez pas.</p>
            </div>
          </a>
          <a href="#coffre" className="l-axis-card l-reveal l-d1">
            <div>
              <h3>Le souvenir que vous envoyez devant</h3>
              <p>Le Coffre garde vos messages pour ses 18 ans, son mariage, sa vie.</p>
            </div>
            <span className="l-axis-arrow">&rarr;</span>
          </a>
        </div>
      </section>

      {/* ============ ACTE 2 — LE PHÉNIX (le quotidien, la preuve) ============ */}
      <section className="l-phenix" id="phenix">
        <div className="l-container">
          <div className="l-section-intro l-reveal">
            <h2>Vos photos prennent le voile du temps.<br/><span className="l-accent">Puis elles renaissent.</span></h2>
            <p>
              Sur Titaton, un souvenir vit d'abord en couleur, dans le présent. Puis, comme les photos
              des vieux albums, il passe doucement en noir et blanc. Rien n'est jamais supprimé —
              il s'endort, c'est tout.
            </p>
          </div>

          <div className="l-phenix-manifesto l-reveal l-d1">
            <p>
              Aujourd'hui, les images perdent leur sens. On scroll, on accumule, on oublie.
              L'historique n'a plus d'effet, plus d'émotion, plus rien de sacré.
            </p>
            <p>
              <strong>Le noir et blanc n'est pas une punition. C'est un acte de respect.</strong>{' '}
              Pour qu'un souvenir vous touche vraiment, il faut d'abord qu'il vous manque.
              Ici, les photos s'endorment — pour que leur retour vous réveille.
            </p>
          </div>

          <div className="l-phenix-grid">
            <div className="l-phenix-copy l-reveal l-d1">
              <h3>Il revient tout seul</h3>
              <p>Un matin, sans prévenir : « il y a un an, jour pour jour ». Le souvenir remonte, en couleur, comme au premier jour. C'est notre façon de vous rendre ce que le temps prend.</p>
              <h3>Ou vous le rallumez vous-même</h3>
              <p>Une photo vous manque ? Ravivez-la — pour vous seul, en toute discrétion. Et si le cœur vous en dit, offrez-la ravivée à tout le cercle : elle remonte en haut du feed, signée de votre nom.</p>
              <p className="l-myth"><PhoenixBird className="l-myth-phoenix" /> On l'a appelé <em>le Phénix</em> — l'oiseau qui renaît de ses cendres.</p>
            </div>

            {/* mini-feed : un souvenir endormi, un souvenir ravivé */}
            <div className="l-minifeed l-reveal l-d2" aria-label="Aperçu du feed : un souvenir endormi et un souvenir ravivé par un proche">
              <div className="l-mem l-mem-sleep">
                <MemoryScene variant="cake" />
                <div className="l-mem-badge"><MoonIcon /> endormi</div>
              </div>
              <div className="l-mem l-mem-awake">
                <MemoryScene variant="park" />
                <div className="l-mem-badge l-gold">&#10038; ravivé par Papa</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ACTE 3 — LE COFFRE (le climax) ============ */}
      <section className="l-concept" id="coffre">
        <div className="l-container">
          <div className="l-section-intro l-reveal">
            <h2>Et il y a plus grand encore : <span className="l-accent">le Coffre.</span></h2>
            <p>
              Chaque enfant a un coffre. On y scelle ce qu'on veut lui transmettre — une photo, une lettre,
              un message vocal — pour un jour qu'on choisit. Ses 18 ans, son mariage, ou simplement le jour
              où il aura besoin de savoir qu'on pensait à lui.
            </p>
          </div>
          <div className="l-timeline-cards">
            <div className="l-timeline-card l-reveal l-d1">
              <div className="l-timeline-dot">
                <svg className="l-timeline-icon" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="12" cy="12" r="3"/><circle cx="17.5" cy="6.5" r="1.5" fill="#fff" stroke="none"/></svg>
              </div>
              <h3>Aujourd'hui</h3>
              <p>Un grand-père enregistre un message pour les 18 ans de son petit-fils. Une maman écrit une lettre pour le jour du bac. Un parrain dépose une photo et un mot.</p>
              <span className="l-time-label">un geste d'amour</span>
            </div>
            <div className="l-timeline-card l-reveal l-d2">
              <div className="l-timeline-dot">
                <svg className="l-timeline-icon" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M9 10V7a3 3 0 0 1 6 0v3"/><circle cx="12" cy="15.5" r="1.5" fill="#fff" stroke="none"/></svg>
              </div>
              <h3>Le coffre se verrouille</h3>
              <p>Vous choisissez la date d'ouverture. Le cadenas se referme. Plus personne ne peut l'ouvrir avant l'heure — pas même nous. Le temps fait son travail.</p>
              <span className="l-time-label">une promesse inviolable</span>
            </div>
            <div className="l-timeline-card l-reveal l-d3">
              <div className="l-timeline-dot">
                <svg className="l-timeline-icon" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M12 2l2.4 5.2 5.6.7-4.1 3.8 1 5.5L12 14.6 7.1 17.2l1-5.5L4 7.9l5.6-.7Z"/></svg>
              </div>
              <h3>Des années plus tard</h3>
              <p>Votre enfant ouvre le coffre. Il entend la voix de ceux qui l'aimaient, même ceux qui ne sont plus là. Certains messages n'ont pas de prix.</p>
              <span className="l-time-label">la magie opère</span>
            </div>
          </div>
          <div className="l-coffre-mascot l-reveal l-d3">
            <Mascotte variant="sceller" size={110} />
          </div>
          <p className="l-coffre-note l-reveal l-d3">
            Toute la famille peut y prendre sa place : mamie dépose une vidéo, le parrain une lettre, un grand-père un message vocal.{' '}
            <strong className="l-accent">Même absent le jour venu, il sera présent dans le coffre.</strong>
          </p>
        </div>
      </section>

      {/* ============ PROMESSES ============ */}
      <section className="l-promises" id="promises">
        <div className="l-container">
          <div className="l-section-intro l-reveal">
            <h2>Nos promesses <span className="l-accent">sacrées.</span></h2>
            <p>Ce qui ne sera jamais négociable.</p>
          </div>
          <div className="l-promises-grid">
            <div className="l-promise l-reveal l-d1">
              <div className="l-promise-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M9 10V7a3 3 0 0 1 6 0v3"/></svg>
              </div>
              <h3>Le coffret est sacré</h3>
              <p>Quand une capsule est verrouillée, personne ne peut l'ouvrir avant la date choisie. Pas même nous. Pas d'exception.</p>
            </div>
            <div className="l-promise l-reveal l-d2">
              <div className="l-promise-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              </div>
              <h3>Rien n'est jamais supprimé</h3>
              <p>Vos souvenirs s'endorment, ils ne meurent pas. Pas de compte à rebours, pas de photos effacées. Jamais.</p>
            </div>
            <div className="l-promise l-reveal l-d3">
              <div className="l-promise-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <h3>Vos souvenirs vous appartiennent</h3>
              <p>Pas de revente, pas d'entraînement IA, pas de pub ciblée. Vos souvenirs restent les vôtres, point.</p>
            </div>
            <div className="l-promise l-reveal l-d4">
              <div className="l-promise-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 6v6l4 2"/></svg>
              </div>
              <h3>Construit pour durer</h3>
              <p>Une capsule verrouillée pour 2040 sera là en 2040. Nous construisons pour le temps long, pas pour la hype.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICING (synchronisé cadrage v4) ============ */}
      <section className="l-pricing" id="pricing">
        <div className="l-container">
          <div className="l-section-intro l-reveal">
            <h2>Généreux dès le <span className="l-accent">premier jour.</span></h2>
            <p>L'essentiel est gratuit. On ne paie que pour deux gestes — et ils se comprennent en une phrase.</p>
          </div>

          <div className="l-pricing-cards">
            <div className="l-pricing-card l-free l-reveal l-d1">
              <div className="l-plan-name">Gratuit</div>
              <div className="l-plan-price">0 €</div>
              <div className="l-plan-sub">pour toujours</div>
              <ul>
                <li><CheckIcon /> Cercle familial illimité — invitez qui vous voulez</li>
                <li><CheckIcon /> Rien n'est jamais supprimé</li>
                <li><CheckIcon /> Le Phénix ravive vos souvenirs par surprise</li>
                <li><CheckIcon /> 3 réveils par mois, à vous</li>
                <li><CheckIcon /> Contribuez aux capsules quand on vous y invite</li>
              </ul>
              <Link to="/register" className="l-btn l-btn-secondary">C'est parti</Link>
            </div>

            <div className="l-paid-stack l-reveal l-d2">
              <div className="l-paid-card">
                <div className="l-paid-head">
                  <span className="l-plan-name">Rallumeur</span>
                  <span className="l-paid-price">2,50 € <small>/ mois</small></span>
                </div>
                <p>Au prix d'un café, réveils illimités — et chaque souvenir que vous ravivez se rallume pour tout le cercle, signé de votre nom.</p>
                <Link to="/register" className="l-btn l-btn-gold">Devenir Rallumeur</Link>
              </div>
              <div className="l-paid-card">
                <div className="l-paid-head">
                  <span className="l-plan-name">Gardien du Coffre</span>
                  <span className="l-paid-price">25 € <small>une fois</small></span>
                </div>
                <p>Votre place permanente dans le coffre d'un enfant : déposez librement, pour toujours. Les parents ont la leur d'office, évidemment.</p>
                <Link to="/register" className="l-btn l-btn-outline-gold">Prendre ma place</Link>
              </div>
            </div>
          </div>

          <p className="l-pricing-note l-reveal l-d3">
            Chaque place et chaque abonnement peuvent être offerts par un proche.{' '}
            <span className="l-accent">Le plus beau des cadeaux, c'est du temps.</span>
          </p>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section className="l-final-cta">
        <div className="l-container">
          <h2 className="l-reveal">Prêt à déposer <span className="l-accent">votre premier souvenir ?</span></h2>
          <p className="l-reveal l-d1">Dans dix ans, vous serez heureux de l'avoir fait aujourd'hui.</p>
          <Link to="/register" className="l-btn l-btn-primary l-reveal l-d2">Commencer gratuitement</Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="l-footer">
        <div className="l-footer-inner">
          <Link to="/" className="l-brand"><span className="l-brand-dot"></span>Tita<PhoenixBird className="l-brand-phoenix" />on</Link>
          <div className="l-footer-links">
            <Link to="/cgv">CGV</Link>
            <Link to="/confidentialite">Confidentialité</Link>
            <Link to="/mentions-legales">Mentions légales</Link>
          </div>
          <div className="l-footer-love">Fait avec soin pour les familles · © 2026 Titaton</div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
